import type { ReactNode } from 'react';
import Link from 'next/link';
import { Skeleton } from '@savvy-kit/components/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@savvy-kit/components/tooltip';
import { DELAY_STAT_STEP, DELAY_SUPPORTING } from '@savvy-kit/foundations/motion/tokens';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import { ChevronRight } from 'lucide-react';

import type { ValueChangeMotion } from '../common/value-change';
import type { HomeBenchmark, HomeDayChange } from './types';
import { shortBenchmarkLabel } from '../common/utils';
import { ValueChange } from '../common/value-change';
import { HOME_DAY_CHANGE_REASON_MESSAGES } from './constants';
import { DayChangeTooltip } from './day-change-tooltip';
import { dayChangeUpdatedLabel } from './utils';

const TILE_CLASS = 'flex h-full flex-col items-start justify-between gap-1 px-4 py-3 md:px-6';

function statBeat(index: number, offset: number): ValueChangeMotion {
  return { kind: 'stat', delay: offset + DELAY_SUPPORTING + index * DELAY_STAT_STEP };
}

const ROW_CLASS = 'grid grid-cols-2 border-t border-border-light';
const CELL_DIVIDER_CLASS = 'border-l border-border-light';
const BENCHMARK_CELL_CLASS =
  'order-last col-span-2 border-t border-border-light md:order-none md:col-span-1 md:border-l md:border-t-0';

type HomeValueTileProps = {
  readonly label: ReactNode;
  readonly fullLabel?: string;
  readonly note?: ReactNode;
  readonly children: ReactNode;
};

function HomeValueTile({ label, fullLabel, note, children }: HomeValueTileProps) {
  const labelText = (
    <Text variant="body-sm" color="secondary" className="overflow-hidden text-ellipsis md:whitespace-nowrap">
      {label}
    </Text>
  );

  return (
    <div className={TILE_CLASS}>
      <div className="flex w-full min-w-0 items-center gap-1.5">
        {fullLabel === undefined || fullLabel === label ? (
          labelText
        ) : (
          <Tooltip>
            <TooltipTrigger render={labelText} />
            <TooltipContent>{fullLabel}</TooltipContent>
          </Tooltip>
        )}
        {note}
      </div>
      {children}
    </div>
  );
}

function benchmarkTile(benchmark: HomeBenchmark, offset: number, beatIndex: number) {
  return (
    <HomeValueTile label={`vs ${shortBenchmarkLabel(benchmark.label)}`} fullLabel={`vs ${benchmark.label}`}>
      <ValueChange percent={benchmark.percent} motion={statBeat(beatIndex, offset)} />
    </HomeValueTile>
  );
}

type TileRowProps = {
  readonly dayChange: ReactNode;
  readonly benchmark: ReactNode;
  readonly totalReturn: ReactNode;
};

function TileRow({ dayChange, benchmark, totalReturn }: TileRowProps) {
  return (
    <div className={cn(ROW_CLASS, benchmark !== null && 'md:grid-cols-3')}>
      <div className="min-w-0">{dayChange}</div>
      {benchmark !== null && <div className={cn('min-w-0', BENCHMARK_CELL_CLASS)}>{benchmark}</div>}
      <div className={cn('min-w-0', CELL_DIVIDER_CLASS)}>{totalReturn}</div>
    </div>
  );
}

type HomeValueTilesProps = {
  readonly dayChange: HomeDayChange;
  readonly totalReturnAmount: number | null;
  readonly totalReturnPercent: number | null;
  readonly benchmarks: readonly HomeBenchmark[];
  readonly investmentsHref: string | null;
  readonly loading: boolean;
  readonly delayOffset?: number;
};

export function HomeValueTiles({
  dayChange,
  totalReturnAmount,
  totalReturnPercent,
  benchmarks,
  investmentsHref,
  loading,
  delayOffset = 0,
}: HomeValueTilesProps) {
  if (loading) {
    return (
      <TileRow
        dayChange={
          <HomeValueTile label={<Skeleton className="h-4 w-16" />}>
            <Skeleton className="h-5 w-24" />
          </HomeValueTile>
        }
        benchmark={
          <HomeValueTile label={<Skeleton className="h-4 w-16" />}>
            <Skeleton className="h-5 w-24" />
          </HomeValueTile>
        }
        totalReturn={
          <HomeValueTile label={<Skeleton className="h-4 w-20" />}>
            <Skeleton className="h-5 w-24" />
          </HomeValueTile>
        }
      />
    );
  }

  const hasSingleBenchmark = benchmarks.length === 1;
  const hasManyBenchmarks = benchmarks.length > 1;
  const totalReturnBeat = hasSingleBenchmark ? 2 : 1;

  const totalReturnTile = (
    <HomeValueTile label="Total return">
      <ValueChange
        amount={totalReturnAmount}
        percent={totalReturnPercent}
        layout="stacked"
        motion={statBeat(totalReturnBeat, delayOffset)}
      />
    </HomeValueTile>
  );

  const dayChangeTile = (
    <HomeValueTile label={dayChangeUpdatedLabel(dayChange)} note={<DayChangeTooltip dayChange={dayChange} />}>
      {dayChange.kind === 'change' ? (
        <ValueChange
          amount={dayChange.amount}
          percent={dayChange.percent}
          layout="stacked"
          motion={statBeat(0, delayOffset)}
        />
      ) : (
        <Text variant="body-sm" color="secondary">
          {HOME_DAY_CHANGE_REASON_MESSAGES[dayChange.reason]}
        </Text>
      )}
    </HomeValueTile>
  );

  const totalReturnNode =
    investmentsHref === null ? (
      totalReturnTile
    ) : (
      <Link
        href={investmentsHref}
        className="group flex h-full items-stretch justify-between transition-colors hover:bg-[var(--component-hover)]"
      >
        {totalReturnTile}
        <ChevronRight size={16} className="mr-4 shrink-0 self-center text-secondary group-hover:text-primary md:mr-6" />
      </Link>
    );

  return (
    <>
      <TileRow
        dayChange={dayChangeTile}
        benchmark={
          hasSingleBenchmark && benchmarks[0] !== undefined ? benchmarkTile(benchmarks[0], delayOffset, 1) : null
        }
        totalReturn={totalReturnNode}
      />
      {hasManyBenchmarks && (
        <div
          className={cn(
            'grid border-t border-border-light',
            benchmarks.length === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-3',
          )}
        >
          {benchmarks.map((benchmark, index) => (
            <div
              key={benchmark.label}
              className={cn('min-w-0', index > 0 && 'border-t border-border-light md:border-l md:border-t-0')}
            >
              {benchmarkTile(benchmark, delayOffset, index + 1)}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
